import { cn } from "@/lib/cn";
import { getMobileMediaCapVars, useMediaLazyLoad } from "@/shared";
import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { environment } from "@environment";
import { useMemo, type CSSProperties } from "react";
import { ElementStyled, WrapperStyled } from "../styles/BeforeAfter.styled";
import type { EditorDataProps, EditorSlotsProps, TBeforeAfter } from "../types";
import { Element } from "./Element";

const PLACEHOLDER_IMAGE = `${environment.base}/assets/images/image-placeholder.jpg`;

export const BeforeAfterLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const { view } = useViewContext();
  const isEditor = view === "editor";
  const { settings } = entity as StaticEntityType<TBeforeAfter>;
  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;
  const { setObserverRef, isVisible } = useMediaLazyLoad();

  const wrapperStyle = useMemo(() => {
    const isNarrowCombo = entity.layers?.combo?.settings?.maxWidth === "narrow";
    return {
      "--entity-width": `${width || 100}%`,
      "--entity-height":
        height && settings.shape === "axis-resize" ? `${height}px` : "auto",
      ...getMobileMediaCapVars(width, settings.shape, isNarrowCombo),
    } as CSSProperties;
  }, [width, height, settings.shape, entity.layers?.combo?.settings?.maxWidth]);

  return (
    <>
      <WrapperStyled
        $alignment={settings.horizontalAlignment}
        ref={(r) => {
          const elementRef = editorData?.elementRef;
          if (elementRef) elementRef.current = r;
          setObserverRef(r);
        }}
        className="tw-flex tw-items-center"
        style={wrapperStyle}
      >
        <div
          ref={editorData?.containerRef}
          className="tw-h-[var(--entity-height)] tw-w-[var(--entity-width)]"
        >
          <ElementStyled
            $round={settings.round}
            $shadow={settings.shadow ?? "none"}
            $shape={settings.shape}
            className={cn("tw-relative tw-size-full tw-overflow-hidden", {
              "tw-pt-[56.25%]": settings.shape === "16-9",
              "tw-pt-[100%]": ["square", "circle"].includes(settings.shape),
              "tw-rounded-full": settings.shape === "circle",
            })}
          >
            <div
              className={cn(
                "tw-absolute tw-inset-0 tw-size-full tw-cursor-ew-resize tw-select-none tw-overflow-hidden",
                {
                  "tw-pointer-events-none": isEditor && !editorData?.isActive,
                },
              )}
            >
              <Element
                shape={settings.shape}
                isActive={editorData?.isActive}
                isVisible={isVisible}
                label={{
                  labelTextColor: settings.labelTextColor,
                  labelBackgroundColor: settings.labelBackgroundColor,
                  labelBackgroundOpacity: settings.labelBackgroundOpacity,
                  showLabels: settings.showLabels,
                  labelsPosition: settings.labelsPosition,
                }}
                before={{
                  src: settings.src_1 || PLACEHOLDER_IMAGE,
                  overlayColor: settings.overlayColor_1,
                  overlayOpacity: settings.overlayOpacity_1,
                  overlay: settings.overlay_1,
                  alt: settings.alt_1,
                  label: settings.label_1,
                  blackAndWhite: settings.blackAndWhite_1,
                }}
                after={{
                  src: settings.src_2 || PLACEHOLDER_IMAGE,
                  overlayColor: settings.overlayColor_2,
                  overlayOpacity: settings.overlayOpacity_2,
                  overlay: settings.overlay_2,
                  alt: settings.alt_2,
                  label: settings.label_2,
                  blackAndWhite: settings.blackAndWhite_2,
                }}
              />
            </div>
          </ElementStyled>
        </div>
        {editorSlots?.resizeSlot}
      </WrapperStyled>
    </>
  );
};
